/**
 * PlanningResultModal - Full-screen modal for viewing saved planning results.
 *
 * Opens directly into a full-screen view showing all saved results for a
 * scenario, grouped by type (KPIs, CBA, Optimization).
 * Charts and cards match the original results pages exactly.
 *
 * Props:
 *   - show: boolean
 *   - onHide: () => void
 *   - scenarioId: string - scenario UUID to filter results
 *   - communityId: string - community UUID for API calls
 *   - scenarioName: string
 *   - currency: string - e.g. 'EUR', 'CZK'
 */

import React, { useState, useEffect, useCallback } from 'react'
import {
  Modal, Button, Badge, Spinner, Alert, Card, Row, Col, Tabs, Tab,
} from 'react-bootstrap'
import Chart from 'react-apexcharts'
import { resultsAPI } from '../api/results'
import { exportOptimizationResults, exportCBAResults, exportLongTermResults } from '../utils/exportCSV'
import BeforeAfterKPI from './charts/BeforeAfterKPI'
import EnergyBalanceChart from './charts/EnergyBalanceChart'
import { useAuth } from '../context/AuthContext'
import { useTranslation } from 'react-i18next'

const FX_RATES = { CZK: 25, EUR: 1, PLN: 4.3, HUF: 395, RON: 5, BGN: 1.96 }

const typeBadgeColors = {
  kpis: { bg: '#E3F2FD', color: '#1565C0' },
  cba: { bg: '#E8F5E9', color: '#2E7D32' },
  optimization: { bg: '#FFF3E0', color: '#E65100' },
  forecast: { bg: '#F3E5F5', color: '#7B1FA2' },
}

// Shared formatting helpers (matching original pages)
const fmtK = (v) => v != null ? `${(v / 1000).toFixed(0)}k` : ''
const fmtV = (v, sym) => v != null ? `${v.toLocaleString()} ${sym}` : ''

// ---------------------------------------------------------------------------
// KPI (Long-Term) Result Detail — matches LongTermResultsPage
// ---------------------------------------------------------------------------
function KPIResultDetail({ result, currency }) {
  const { t } = useTranslation()
  const rd = result.result_data || {}
  const fxRate = FX_RATES[currency] || 1
  const sym = currency || 'EUR'

  // Data is saved as { withInvestment: {...}, existingOnly: {...} }
  const inv = rd.withInvestment || rd
  const baseline = rd.existingOnly || null
  const hasBaseline = !!baseline

  const years = inv.years || inv.year_labels || []
  const selfSufficiency = inv.selfSufficiency || []
  const selfConsumption = inv.selfConsumption || []
  const gridDependence = inv.gridDependence || selfSufficiency.map(v => +(100 - v).toFixed(1))
  const cumulativeSavings = inv.cumulativeSavings || []
  const cumulativeSCSavings = inv.cumulativeSCSavings || []
  const cumulativeExportRevenue = inv.cumulativeExportRevenue || []
  const summary = inv.summary || {}

  const selfConsumedKwh = inv.selfConsumedKwh || []
  const gridImportKwh = inv.gridImportKwh || []
  const gridExportKwh = inv.gridExportKwh || []

  if (years.length === 0 && Object.keys(summary).length === 0) {
    return <p className="text-muted">{t('result_modal.no_kpi_data')}</p>
  }

  // --- Summary KPI Cards (matching LongTermResultsPage) ---
  const summaryCards = [
    { label: t('results.total_savings'), value: summary.totalSavings != null ? fmtK(summary.totalSavings) : null, color: 'var(--color-success)',
      baseline: hasBaseline && baseline.summary?.totalSavings != null ? t('result_modal.card_baseline_savings', { value: fmtK(baseline.summary.totalSavings), delta: fmtK(summary.totalSavings - baseline.summary.totalSavings) }) : null },
    { label: t('results.avg_self_sufficiency'), value: summary.avgSelfSufficiency != null ? `${Number(summary.avgSelfSufficiency).toFixed(1)}%` : null, color: 'var(--color-primary)',
      baseline: hasBaseline && baseline.summary?.avgSelfSufficiency != null ? t('result_modal.card_baseline_ss', { value: baseline.summary.avgSelfSufficiency.toFixed(1), delta: (summary.avgSelfSufficiency - baseline.summary.avgSelfSufficiency).toFixed(1) }) : null },
    { label: t('results.total_co2_avoided'), value: summary.totalCO2Avoided != null ? t('results.tons', { value: Number(summary.totalCO2Avoided).toFixed(0) }) : null, color: 'var(--color-success)',
      baseline: hasBaseline && baseline.summary?.totalCO2Avoided != null ? t('result_modal.card_baseline_co2', { value: baseline.summary.totalCO2Avoided.toFixed(0), delta: (summary.totalCO2Avoided - baseline.summary.totalCO2Avoided).toFixed(1) }) : null },
    { label: t('results.roi'), value: summary.roi != null ? `${Number(summary.roi)}%` : null, color: 'var(--color-accent)',
      baseline: t('results.roi_note') },
  ].filter(c => c.value != null)

  // --- KPI Projections chart ---
  const kpiProjectionOptions = {
    chart: { type: 'line', height: 350, toolbar: { show: true } },
    colors: ['#8CB808', '#27AE60', '#E74C3C'],
    stroke: { curve: 'smooth', width: 2.5, dashArray: [0, 0, 0] },
    dataLabels: { enabled: false },
    legend: { position: 'top', fontSize: '12px' },
    xaxis: { categories: years, title: { text: t('results.year') }, labels: { style: { fontSize: '11px' } } },
    yaxis: { title: { text: t('results.percentage') }, min: 0, max: 100, labels: { formatter: v => v != null ? v.toFixed(0) : '' } },
    tooltip: { shared: true, intersect: false, y: { formatter: v => v != null ? `${v.toFixed(1)}%` : '' } },
    grid: { borderColor: '#e0e0e0', strokeDashArray: 3 },
  }
  const kpiProjectionSeries = [
    { name: t('results.self_sufficiency'), data: selfSufficiency },
    { name: t('results.self_consumption'), data: selfConsumption },
    { name: t('results.grid_dependence'), data: gridDependence },
  ]

  if (hasBaseline) {
    const bSS = baseline.selfSufficiency || []
    const bSC = baseline.selfConsumption || []
    const bGD = baseline.gridDependence || bSS.map(v => +(100 - v).toFixed(1))
    if (bSS.length > 0) {
      kpiProjectionSeries.push({ name: t('result_modal.series_ss_existing'), data: bSS })
      kpiProjectionOptions.colors.push('#8CB80860')
    }
    if (bSC.length > 0) {
      kpiProjectionSeries.push({ name: t('result_modal.series_sc_existing'), data: bSC })
      kpiProjectionOptions.colors.push('#27AE6060')
    }
    if (bGD.length > 0) {
      kpiProjectionSeries.push({ name: t('result_modal.series_gd_existing'), data: bGD })
      kpiProjectionOptions.colors.push('#E74C3C60')
    }
    // Dashed lines for baseline series
    const numInv = 3
    const numBase = kpiProjectionSeries.length - numInv
    kpiProjectionOptions.stroke.dashArray = [...Array(numInv).fill(0), ...Array(numBase).fill(5)]
  }

  // --- Energy Flows grouped bar chart ---
  const hasEnergyFlows = selfConsumedKwh.length > 0 || gridImportKwh.length > 0
  const energyFlowSeries = [
    { name: t('result_modal.series_self_consumed_inv'), data: selfConsumedKwh, group: 'investment' },
    { name: t('result_modal.series_grid_import_inv'), data: gridImportKwh, group: 'investment' },
    { name: t('result_modal.series_grid_export_inv'), data: gridExportKwh.map(v => -v), group: 'investment' },
  ]
  const energyFlowColors = ['#8CB808', '#3498DB', '#E67E22']
  if (hasBaseline && baseline.selfConsumedKwh?.length > 0) {
    energyFlowSeries.push(
      { name: t('result_modal.series_self_consumed_existing'), data: baseline.selfConsumedKwh, group: 'existing' },
      { name: t('result_modal.series_grid_import_existing'), data: baseline.gridImportKwh || [], group: 'existing' },
      { name: t('result_modal.series_grid_export_existing'), data: (baseline.gridExportKwh || []).map(v => -v), group: 'existing' },
    )
    energyFlowColors.push('#8CB80880', '#3498DB80', '#E67E2280')
  }
  const energyFlowOptions = {
    chart: { type: 'bar', height: 350, stacked: true, toolbar: { show: true } },
    colors: energyFlowColors,
    plotOptions: { bar: { horizontal: false, columnWidth: '55%' } },
    dataLabels: { enabled: false },
    legend: { position: 'top', fontSize: '11px' },
    xaxis: { categories: years, title: { text: t('results.year') } },
    yaxis: { title: { text: t('results.energy_mwh') }, labels: { formatter: v => v != null ? `${(v / 1000).toFixed(0)}` : '' } },
    tooltip: { shared: true, intersect: false, y: { formatter: v => v != null ? `${Math.abs(v).toLocaleString()} kWh` : '' } },
  }

  // --- Cumulative Savings area chart ---
  const hasSavingsBreakdown = cumulativeSCSavings.length > 0
  const savingsSeries = hasSavingsBreakdown
    ? [
        { name: t('results.avoided_import_cost'), data: cumulativeSCSavings },
        { name: t('results.export_revenue'), data: cumulativeExportRevenue },
      ]
    : [{ name: t('result_modal.series_cumulative_savings'), data: cumulativeSavings }]
  const savingsColors = hasSavingsBreakdown ? ['#7B2D8E', '#2196F3'] : ['#8CB808']

  if (hasBaseline) {
    if (baseline.cumulativeSCSavings?.length > 0) {
      savingsSeries.push({ name: t('result_modal.series_avoided_import_existing'), data: baseline.cumulativeSCSavings })
      savingsColors.push('#7B2D8E60')
    }
    if (baseline.cumulativeExportRevenue?.length > 0) {
      savingsSeries.push({ name: t('result_modal.series_export_revenue_existing'), data: baseline.cumulativeExportRevenue })
      savingsColors.push('#2196F380')
    } else if (baseline.cumulativeSavings?.length > 0) {
      savingsSeries.push({ name: t('result_modal.series_existing_only'), data: baseline.cumulativeSavings })
      savingsColors.push('#999')
    }
  }

  const savingsOptions = {
    chart: { type: 'area', height: 350, stacked: hasSavingsBreakdown, toolbar: { show: true } },
    colors: savingsColors,
    stroke: { curve: 'smooth', width: 2.5 },
    fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.05 } },
    dataLabels: { enabled: false },
    legend: { position: 'top', fontSize: '12px' },
    xaxis: { categories: years, title: { text: t('results.year') } },
    yaxis: { title: { text: t('result_modal.savings_axis', { currency: sym }) }, labels: { formatter: v => v != null ? fmtK(v) : '' } },
    tooltip: { y: { formatter: v => v != null ? fmtV(v, sym) : '' } },
  }

  return (
    <>
      {/* Summary KPI Cards */}
      {summaryCards.length > 0 && (
        <Row className="g-3 mb-4">
          {summaryCards.map(card => (
            <Col key={card.label} sm={6} lg={3}>
              <Card style={{ border: '1px solid var(--color-neutral-200)', borderRadius: 'var(--radius-lg)', height: '100%' }}>
                <Card.Body>
                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-600)', marginBottom: 'var(--space-2)' }}>{card.label}</div>
                  <div style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 700, color: card.color }}>{card.value}</div>
                  {card.baseline && (
                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-500)', marginTop: 2 }}>{card.baseline}</div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* KPI Projections */}
      {years.length > 0 && selfSufficiency.length > 0 && (
        <Card className="mb-4" style={{ border: '1px solid var(--color-neutral-200)', borderRadius: 'var(--radius-lg)' }}>
          <Card.Body>
            <h6 style={{ fontWeight: 600, marginBottom: 'var(--space-3)' }}>
              {t('results.annual_kpi_projections')}
              {hasBaseline && <span style={{ fontWeight: 400, fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-500)', marginLeft: 8 }}>
                {t('results.legend_solid_dashed')}
              </span>}
            </h6>
            <Chart options={kpiProjectionOptions} series={kpiProjectionSeries} type="line" height={350} />
          </Card.Body>
        </Card>
      )}

      {/* Energy Flows + Cumulative Savings row */}
      <Row className="g-4 mb-4">
        {hasEnergyFlows && (
          <Col md={7}>
            <Card style={{ border: '1px solid var(--color-neutral-200)', borderRadius: 'var(--radius-lg)' }}>
              <Card.Body>
                <h6 style={{ fontWeight: 600, marginBottom: 'var(--space-3)' }}>
                  {t('results.annual_energy_flows')}
                  {hasBaseline && <span style={{ fontWeight: 400, fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-500)', marginLeft: 8 }}>
                    {t('results.legend_comparing')}
                  </span>}
                </h6>
                <Chart options={energyFlowOptions} series={energyFlowSeries} type="bar" height={350} />
              </Card.Body>
            </Card>
          </Col>
        )}
        {cumulativeSavings.length > 0 && (
          <Col md={hasEnergyFlows ? 5 : 12}>
            <Card style={{ border: '1px solid var(--color-neutral-200)', borderRadius: 'var(--radius-lg)' }}>
              <Card.Body>
                <h6 style={{ fontWeight: 600, marginBottom: 'var(--space-3)' }}>{t('results.cumulative_cost_savings')}</h6>
                <Chart options={savingsOptions} series={savingsSeries} type="area" height={350} />
              </Card.Body>
            </Card>
          </Col>
        )}
      </Row>
    </>
  )
}

// ---------------------------------------------------------------------------
// CBA Result Detail — matches CBAResultsPage exactly
// ---------------------------------------------------------------------------
function CBAResultDetail({ result, currency }) {
  const { t } = useTranslation()
  const rd = result.result_data || {}
  const sym = currency || 'EUR'
  const [breakdownScenario, setBreakdownScenario] = useState(null)
  const [co2Scenario, setCo2Scenario] = useState(null)

  const scenarios = rd.scenarios || []
  const ccf = rd.cumulativeCashFlow || {}
  const sensitivity = rd.sensitivity || {}
  const annualCostBreakdown = rd.annualCostBreakdown || {}
  const params = result.parameters || {}

  if (scenarios.length === 0) {
    return <p className="text-muted">{t('result_modal.no_cba_data')}</p>
  }

  const sel = rd.selectedScenarios || ['pv_only']
  const investmentScenarios = scenarios.slice(1)
  const bestFinancial = investmentScenarios.length > 0
    ? investmentScenarios.reduce((a, b) => ((b.npv || 0) > (a.npv || 0) ? b : a), investmentScenarios[0])
    : null
  const bestEnvironmental = investmentScenarios.length > 0
    ? investmentScenarios.reduce((a, b) => ((b.co2Avoided || 0) > (a.co2Avoided || 0) ? b : a), investmentScenarios[0])
    : null

  // --- Scenario Comparison Cards (matching CBAResultsPage) ---
  const scenarioColors = {
    'Baseline': { border: 'var(--color-neutral-300)', bg: 'var(--color-neutral-100)', text: 'var(--color-neutral-700)' },
    'PV Only': { border: 'var(--color-primary)', bg: 'var(--color-primary)', text: '#fff' },
    'BESS Only': { border: '#2196F3', bg: '#2196F3', text: '#fff' },
    'PV + BESS': { border: 'var(--color-accent)', bg: 'var(--color-accent)', text: '#fff' },
  }

  // --- Cumulative Cash Flow chart with break-even annotation ---
  const cfScenarioColors = { 'Baseline': '#9CA3AF', 'PV Only': '#8CB808', 'BESS Only': '#2196F3', 'PV + BESS': '#5C5C5C' }
  const cashFlowSeries = [
    { name: 'Baseline', data: ccf.baseline || [] },
    ...(ccf.pvOnly?.length > 0 ? [{ name: 'PV Only', data: ccf.pvOnly }] : []),
    ...(ccf.bessOnly?.length > 0 ? [{ name: 'BESS Only', data: ccf.bessOnly }] : []),
    ...(ccf.pvBess?.length > 0 ? [{ name: 'PV + BESS', data: ccf.pvBess }] : []),
  ]
  const cfColors = cashFlowSeries.map(s => cfScenarioColors[s.name] || '#666')
  const hasCCF = (ccf.years || []).length > 0

  const cashFlowChartOptions = {
    chart: { type: 'line', height: 350, toolbar: { show: true }, animations: { enabled: true, speed: 800 } },
    colors: cfColors,
    stroke: { width: cashFlowSeries.map((_, i) => i === 0 ? 2 : 3), curve: 'smooth', dashArray: cashFlowSeries.map((_, i) => i === 0 ? 5 : 0) },
    dataLabels: { enabled: false },
    legend: { position: 'top', horizontalAlign: 'left' },
    xaxis: { categories: ccf.years || [], title: { text: t('results.year') } },
    yaxis: { title: { text: t('cba_results.ccf_axis', { currency: sym }) }, labels: { formatter: v => v != null ? fmtK(v) : '' } },
    markers: { size: 0, hover: { size: 6 } },
    tooltip: { y: { formatter: v => v != null ? fmtV(v, sym) : '' } },
    grid: { borderColor: '#e0e0e0', strokeDashArray: 3 },
    annotations: { yaxis: [{ y: 0, borderColor: '#000', strokeDashArray: 2, label: { text: t('results.break_even'), style: { color: '#000', fontSize: '10px' } } }] },
  }

  // --- Sensitivity Analysis Table (matching CBAResultsPage — TABLE, not chart) ---
  const sensitivityColumns = [
    ...(sel.includes('pv_only') ? [{ key: 'pvOnly', label: 'PV Only', color: 'var(--color-primary)' }] : []),
    ...(sel.includes('bess_only') ? [{ key: 'bessOnly', label: 'BESS Only', color: '#2196F3' }] : []),
    ...(sel.includes('pv_bess') ? [{ key: 'pvBess', label: 'PV + BESS', color: 'var(--color-accent)' }] : []),
  ]
  // Fallback: if no sel info, show all columns that have data
  if (sensitivityColumns.length === 0) {
    if (sensitivity.pvOnly?.length > 0) sensitivityColumns.push({ key: 'pvOnly', label: 'PV Only', color: 'var(--color-primary)' })
    if (sensitivity.bessOnly?.length > 0) sensitivityColumns.push({ key: 'bessOnly', label: 'BESS Only', color: '#2196F3' })
    if (sensitivity.pvBess?.length > 0) sensitivityColumns.push({ key: 'pvBess', label: 'PV + BESS', color: 'var(--color-accent)' })
  }
  const hasSensitivity = (sensitivity.discountRates || []).length > 0

  // --- Cost vs Benefits breakdown ---
  const breakdownScenarioOptions = [
    { key: 'baseline', label: 'Baseline' },
    ...(annualCostBreakdown.pvOnly ? [{ key: 'pvOnly', label: 'PV Only' }] : []),
    ...(annualCostBreakdown.bessOnly ? [{ key: 'bessOnly', label: 'BESS Only' }] : []),
    ...(annualCostBreakdown.pvBess ? [{ key: 'pvBess', label: 'PV + BESS' }] : []),
  ]
  const effectiveBreakdownScenario = breakdownScenario
    || (breakdownScenarioOptions.find(o => o.key !== 'baseline')?.key)
    || 'baseline'
  const hasBreakdown = Object.keys(annualCostBreakdown).length > 0 && annualCostBreakdown.categories?.length > 0

  const totalCards = scenarios.length
  const colSize = totalCards <= 2 ? 6 : totalCards === 3 ? 4 : 3

  return (
    <>
      {/* Scenario Comparison Cards */}
      <Row className="g-3 mb-4">
        {scenarios.map((scenario, idx) => {
          const isBaseline = idx === 0
          const isBest = !isBaseline && bestFinancial && scenario === bestFinancial
          const colors = scenarioColors[scenario.name] || scenarioColors['PV Only']
          return (
            <Col key={idx} md={colSize}>
              <Card style={{ border: `2px solid ${colors.border}`, borderRadius: 'var(--radius-lg)', height: '100%', overflow: 'hidden' }}>
                <div style={{ backgroundColor: colors.bg, color: colors.text, padding: '10px 16px', fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>
                  {scenario.name}
                  {isBest && investmentScenarios.length > 1 && <span style={{ marginLeft: 8, fontSize: 'var(--font-size-xs)', opacity: 0.9 }}>{t('results.best_roi')}</span>}
                </div>
                <Card.Body>
                  <div className="d-flex flex-column gap-2" style={{ fontSize: 'var(--font-size-sm)' }}>
                    <div className="d-flex justify-content-between">
                      <span style={{ color: 'var(--color-neutral-600)' }}>{t('results.npv_colon')}</span>
                      <span style={{ fontWeight: 700, color: (scenario.npv || 0) > 0 ? 'var(--color-success)' : 'var(--color-neutral-700)' }}>
                        {isBaseline ? t('results.baseline') : `${(scenario.npv || 0).toLocaleString()} ${sym}`}
                      </span>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span style={{ color: 'var(--color-neutral-600)' }}>{t('results.bcr_colon')}</span>
                      <span style={{ fontWeight: 600 }}>{isBaseline ? '\u2014' : (scenario.bcr || 0).toFixed(1)}</span>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span style={{ color: 'var(--color-neutral-600)' }}>{t('results.payback_colon')}</span>
                      <span style={{ fontWeight: 600 }}>{isBaseline ? '\u2014' : scenario.payback ? t('results.payback_years', { years: scenario.payback.toFixed(1) }) : t('results.na')}</span>
                    </div>
                    <div className="d-flex justify-content-between" style={{ borderTop: '1px solid var(--color-neutral-200)', paddingTop: 'var(--space-2)', marginTop: 'var(--space-1)' }}>
                      <span style={{ color: 'var(--color-neutral-600)' }}>{isBaseline ? t('results.annual_grid_cost_colon') : t('results.annual_savings_colon')}</span>
                      <span style={{ fontWeight: 600 }}>
                        {isBaseline
                          ? `${(scenario.annualGridCost || 0).toLocaleString()} ${sym}`
                          : `${(scenario.annualSavings || 0).toLocaleString()} ${sym}`}
                      </span>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span style={{ color: 'var(--color-neutral-600)' }}>{t('results.co2_avoided_colon')}</span>
                      <span style={{ fontWeight: 600, color: (scenario.co2Avoided || 0) > 0 ? 'var(--color-primary)' : 'inherit' }}>
                        {isBaseline ? '\u2014' : t('results.t_per_year', { value: (scenario.co2Avoided || 0).toFixed(1) })}
                      </span>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          )
        })}
      </Row>

      {/* Charts Row 1: Cumulative Cash Flow + Sensitivity Table */}
      <Row className="g-4 mb-4">
        {hasCCF && (
          <Col md={8}>
            <Card style={{ border: '1px solid var(--color-neutral-200)', borderRadius: 'var(--radius-lg)' }}>
              <Card.Body>
                <h6 style={{ fontWeight: 600, marginBottom: 'var(--space-3)' }}>{t('results.cumulative_cash_flow')}</h6>
                <Chart options={cashFlowChartOptions} series={cashFlowSeries} type="line" height={350} />
              </Card.Body>
            </Card>
          </Col>
        )}
        {hasSensitivity && (
          <Col md={hasCCF ? 4 : 12}>
            <Card style={{ border: '1px solid var(--color-neutral-200)', borderRadius: 'var(--radius-lg)', height: '100%' }}>
              <Card.Body>
                <h6 style={{ fontWeight: 600, marginBottom: 'var(--space-3)' }}>{t('results.sensitivity_analysis')}</h6>
                <table className="mb-0" style={{ width: '100%', fontSize: 'var(--font-size-xs)', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: 'var(--color-neutral-100)', borderBottom: '2px solid var(--color-neutral-300)' }}>
                      <th style={{ padding: '8px 10px', fontWeight: 600, color: 'var(--color-neutral-700)' }}>{t('results.discount_rate')}</th>
                      {sensitivityColumns.map(col => (
                        <th key={col.key} style={{ padding: '8px 10px', fontWeight: 600, color: col.color, textAlign: 'right' }}>{col.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sensitivity.discountRates.map((rate, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid var(--color-neutral-200)' }}>
                        <td style={{ padding: '6px 10px', color: 'var(--color-neutral-700)' }}>{rate}%</td>
                        {sensitivityColumns.map(col => (
                          <td key={col.key} style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 500 }}>
                            {fmtK(sensitivity[col.key]?.[idx])}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="mt-3" style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-500)', lineHeight: 1.5 }}>
                  <strong style={{ color: 'var(--color-neutral-700)' }}>{t('results.how_to_read_label')}</strong> {t('result_modal.how_to_read_text')}
                </div>
              </Card.Body>
            </Card>
          </Col>
        )}
      </Row>

      {/* Charts Row 2: Cost vs Benefits + CO2 Impact */}
      <Row className="g-4 mb-4">
        {hasBreakdown && (
          <Col md={8}>
            <Card style={{ border: '1px solid var(--color-neutral-200)', borderRadius: 'var(--radius-lg)' }}>
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: 'var(--space-3)' }}>
                  <h6 style={{ fontWeight: 600, margin: 0 }}>{t('results.costs_vs_benefits')}</h6>
                  <select
                    value={effectiveBreakdownScenario}
                    onChange={(e) => setBreakdownScenario(e.target.value)}
                    style={{ fontSize: 'var(--font-size-sm)', padding: '4px 10px', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-neutral-300)', backgroundColor: 'var(--color-neutral-50)', color: 'var(--color-neutral-700)', cursor: 'pointer' }}
                  >
                    {breakdownScenarioOptions.map(opt => (
                      <option key={opt.key} value={opt.key}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                {(() => {
                  const bd = annualCostBreakdown[effectiveBreakdownScenario] || {}
                  const hasBatt = (bd.batteryValue || []).some(v => v > 0)
                  const hasExport = (bd.exportRevenue || []).some(v => v > 0)
                  const hasReplacement = (bd.replacementCost || []).some(v => v > 0)
                  const hasCapex = (bd.capex || 0) > 0
                  const numCategories = (annualCostBreakdown.categories || []).length

                  const capexData = numCategories > 0 ? [-(bd.capex || 0), ...Array(numCategories - 1).fill(0)] : []

                  const series = [
                    ...(hasCapex ? [{ name: t('results.capex_series'), data: capexData }] : []),
                    { name: t('results.grid_import'), data: (bd.gridImport || []).map(v => -v) },
                    { name: t('results.om_series'), data: (bd.om || []).map(v => -v) },
                    ...(hasReplacement ? [{ name: t('results.battery_replacement'), data: (bd.replacementCost || []).map(v => -v) }] : []),
                    { name: t('results.pv_self_consumption_savings'), data: bd.pvValue || [] },
                    ...(hasBatt ? [{ name: t('results.battery_savings'), data: bd.batteryValue || [] }] : []),
                    ...(hasExport ? [{ name: t('results.export_revenue'), data: bd.exportRevenue || [] }] : []),
                  ]
                  const costColors = []
                  if (hasCapex) costColors.push('#7B2D8E')
                  costColors.push('#DC3545', '#9CA3AF')
                  if (hasReplacement) costColors.push('#FF8C00')
                  const benefitColors = ['#8CB808']
                  if (hasBatt) benefitColors.push('#2196F3')
                  if (hasExport) benefitColors.push('#66BB6A')
                  const colors = [...costColors, ...benefitColors]

                  const options = {
                    chart: { type: 'bar', height: 350, stacked: true, toolbar: { show: false } },
                    colors,
                    plotOptions: { bar: { horizontal: false, columnWidth: '65%' } },
                    dataLabels: { enabled: false },
                    legend: { position: 'top', horizontalAlign: 'left', fontSize: '11px' },
                    xaxis: { categories: annualCostBreakdown.categories },
                    yaxis: { title: { text: t('cba_results.cumulative_axis', { currency: sym }) }, labels: { formatter: v => v != null ? fmtK(v) : '' } },
                    tooltip: { y: { formatter: v => v != null ? `${Math.abs(v).toLocaleString()} ${sym}` : '' } },
                    fill: { opacity: 0.85 },
                    annotations: { yaxis: [{ y: 0, borderColor: '#333', strokeDashArray: 0, borderWidth: 1 }] },
                  }

                  return <Chart options={options} series={series} type="bar" height={350} />
                })()}
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-500)', marginTop: 'var(--space-2)' }}>
                  {t('results.breakdown_note')}
                </div>
              </Card.Body>
            </Card>
          </Col>
        )}
        {investmentScenarios.length > 0 && (
          <Col md={hasBreakdown ? 4 : 12}>
            <Card style={{ border: '1px solid var(--color-neutral-200)', borderRadius: 'var(--radius-lg)', height: '100%' }}>
              <Card.Body>
                {(() => {
                  const co2Options = investmentScenarios.map((s, i) => ({ idx: i, ...s }))
                  const selectedIdx = co2Scenario != null ? co2Scenario : co2Options.findIndex(s => bestEnvironmental && s.name === bestEnvironmental.name)
                  const sc = co2Options[selectedIdx >= 0 ? selectedIdx : 0]
                  if (!sc) return null
                  const lifetimeCO2 = sc.lifetimeCO2Avoided != null ? sc.lifetimeCO2Avoided : (sc.co2Avoided || 0) * (params.analysisPeriodYears || 20)
                  const trees = sc.equivalentTreesPlanted != null ? sc.equivalentTreesPlanted : lifetimeCO2 * 40

                  return (
                    <>
                      <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: 'var(--space-3)' }}>
                        <h6 style={{ fontWeight: 600, margin: 0 }}>{t('results.co2_impact')}</h6>
                        {co2Options.length > 1 && (
                          <select
                            value={selectedIdx >= 0 ? selectedIdx : 0}
                            onChange={(e) => setCo2Scenario(Number(e.target.value))}
                            style={{ fontSize: 'var(--font-size-sm)', padding: '4px 10px', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-neutral-300)', backgroundColor: 'var(--color-neutral-50)', color: 'var(--color-neutral-700)', cursor: 'pointer' }}
                          >
                            {co2Options.map((opt, i) => (
                              <option key={i} value={i}>{opt.name}</option>
                            ))}
                          </select>
                        )}
                      </div>
                      <div className="d-flex flex-column gap-3">
                        <div>
                          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-600)' }}>{t('results.lifetime_co2_avoided')}</div>
                          <div style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 700, color: 'var(--color-success)' }}>
                            {t('results.tons', { value: lifetimeCO2.toFixed(0) })}
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-600)' }}>{t('results.annual_co2_reduction')}</div>
                          <div style={{ fontSize: 'var(--font-size-xl)', fontWeight: 600 }}>
                            {t('results.tons_per_year', { value: (sc.co2Avoided || 0).toFixed(1) })}
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-600)' }}>{t('results.equivalent_trees')}</div>
                          <div style={{ fontSize: 'var(--font-size-xl)', fontWeight: 600 }}>
                            {t('results.trees_count', { count: Math.round(trees).toLocaleString() })}
                          </div>
                          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-500)', marginTop: '4px' }}>
                            {t('results.trees_note')}
                          </div>
                        </div>
                      </div>
                    </>
                  )
                })()}
              </Card.Body>
            </Card>
          </Col>
        )}
      </Row>

      {/* Recommendation Summary */}
      {bestFinancial && (
        <Card className="mb-4" style={{ border: '1px solid var(--color-primary-light)', borderRadius: 'var(--radius-lg)', backgroundColor: 'var(--color-primary-pale)' }}>
          <Card.Body>
            <h6 style={{ fontWeight: 600, color: 'var(--color-primary-dark)', marginBottom: 'var(--space-3)' }}>{t('results.recommendation_summary')}</h6>
            <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-neutral-800)' }}>
              <p style={{ marginBottom: 'var(--space-2)' }}>
                <strong>{t('results.best_financial_return')}</strong> {t('result_modal.rec_financial_detail', {
                  name: bestFinancial.name,
                  npv: (bestFinancial.npv || 0).toLocaleString(),
                  sym,
                  bcr: (bestFinancial.bcr || 0).toFixed(1),
                  payback: bestFinancial.payback?.toFixed(1) ?? t('results.na'),
                })}
              </p>
              {bestEnvironmental && bestEnvironmental !== bestFinancial && (
                <p style={{ marginBottom: 'var(--space-2)' }}>
                  <strong>{t('results.best_environmental_impact')}</strong> {t('result_modal.rec_env_detail', {
                    name: bestEnvironmental.name,
                    co2: (bestEnvironmental.co2Avoided || 0).toFixed(1),
                  })}
                </p>
              )}
            </div>
          </Card.Body>
        </Card>
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// Optimization Result Detail — matches PlanningResultsPage
// ---------------------------------------------------------------------------
function OptimizationResultDetail({ result, currency }) {
  const { t } = useTranslation()
  const rd = result.result_data || {}
  const fxRate = FX_RATES[currency] || 1

  const baselineKPIs = rd.baselineKPIs || rd.baseline_kpis || null
  const optimizedKPIs = rd.optimizedKPIs || rd.optimized_kpis || rd.kpis || null
  const energyBalance = rd.energyBalance || rd.energy_balance || null
  const batterySchedule = rd.batterySchedule || rd.battery_schedule || null

  if (!optimizedKPIs && !energyBalance) {
    return <p className="text-muted">{t('result_modal.no_optimization_data')}</p>
  }

  const hours = Array.from({ length: 24 }, (_, i) => i)
  const batterySoC = batterySchedule?.soc || hours.map(() => 50)
  const batteryCharge = batterySchedule?.charge || hours.map(() => 0)
  const batteryDischarge = batterySchedule?.discharge || hours.map(() => 0)

  const batteryOptions = {
    chart: { type: 'line', height: 320, toolbar: { show: true } },
    colors: ['#8CB808', '#27AE60', '#5C5C5C'],
    stroke: { curve: 'smooth', width: [3, 2, 2] },
    fill: { opacity: [1, 0.3, 0.3] },
    dataLabels: { enabled: false },
    legend: { position: 'top', horizontalAlign: 'left', fontSize: '12px' },
    xaxis: { categories: hours, title: { text: t('results.hour_of_day') }, labels: { style: { fontSize: '11px' } } },
    yaxis: [
      { title: { text: t('results.soc_pct') }, min: 0, max: 100, labels: { formatter: v => v != null ? v.toFixed(0) : '' } },
      { opposite: true, title: { text: t('results.power_kw') }, labels: { formatter: v => v != null ? v.toFixed(1) : '' } },
    ],
    tooltip: { shared: true, intersect: false, y: { formatter: v => v != null ? v.toFixed(2) : '' } },
    grid: { borderColor: '#e0e0e0', strokeDashArray: 3 },
  }
  const batterySeries = [
    { name: t('results.soc_pct'), type: 'line', data: batterySoC },
    { name: t('results.charge_kw'), type: 'area', data: batteryCharge },
    { name: t('results.discharge_kw'), type: 'area', data: batteryDischarge },
  ]

  const kpiCards = optimizedKPIs ? [
    { label: t('results.daily_cost'), value: optimizedKPIs.total_cost_eur != null ? `${(optimizedKPIs.total_cost_eur * fxRate).toFixed(2)} ${currency}` : optimizedKPIs.daily_cost_eur != null ? `${(optimizedKPIs.daily_cost_eur * fxRate).toFixed(2)} ${currency}` : null, color: 'var(--color-success)' },
    { label: t('results.self_sufficiency'), value: optimizedKPIs.self_sufficiency_pct != null ? `${optimizedKPIs.self_sufficiency_pct.toFixed(1)}%` : null, color: 'var(--color-primary)' },
    { label: t('results.grid_import'), value: optimizedKPIs.total_grid_import_kwh != null ? `${optimizedKPIs.total_grid_import_kwh.toFixed(1)} kWh` : optimizedKPIs.grid_import_kwh != null ? `${optimizedKPIs.grid_import_kwh.toFixed(0)} kWh` : null, color: 'var(--color-neutral-800)' },
    { label: t('results.co2_avoided'), value: optimizedKPIs.co2_avoided_kg != null ? `${optimizedKPIs.co2_avoided_kg.toFixed(1)} kg` : null, color: 'var(--color-primary)' },
  ].filter(c => c.value != null) : []

  return (
    <>
      {kpiCards.length > 0 && (
        <Row className="g-3 mb-4">
          {kpiCards.map(card => (
            <Col key={card.label} sm={6} lg={3}>
              <Card style={{ border: '1px solid var(--color-neutral-200)', borderRadius: 'var(--radius-lg)' }}>
                <Card.Body className="p-3">
                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-600)' }}>{card.label}</div>
                  <div style={{ fontSize: 'var(--font-size-xl)', fontWeight: 700, color: card.color }}>{card.value}</div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {baselineKPIs && optimizedKPIs && (
        <Card className="mb-4" style={{ border: '1px solid var(--color-neutral-200)', borderRadius: 'var(--radius-lg)' }}>
          <Card.Body>
            <h6 style={{ fontWeight: 600, marginBottom: 'var(--space-3)' }}>{t('results.optimization_impact')}</h6>
            <BeforeAfterKPI baselineKPIs={baselineKPIs} optimizedKPIs={optimizedKPIs} currency={currency} />
          </Card.Body>
        </Card>
      )}

      {energyBalance && (
        <Card className="mb-4" style={{ border: '1px solid var(--color-neutral-200)', borderRadius: 'var(--radius-lg)' }}>
          <Card.Body>
            <h6 style={{ fontWeight: 600, marginBottom: 'var(--space-3)' }}>{t('results.energy_balance')}</h6>
            <EnergyBalanceChart data={energyBalance} />
          </Card.Body>
        </Card>
      )}

      {batterySchedule && (
        <Card className="mb-4" style={{ border: '1px solid var(--color-neutral-200)', borderRadius: 'var(--radius-lg)' }}>
          <Card.Body>
            <h6 style={{ fontWeight: 600, marginBottom: 'var(--space-3)' }}>{t('results.battery_schedule')}</h6>
            <Chart options={batteryOptions} series={batterySeries} type="line" height={320} />
          </Card.Body>
        </Card>
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// Export helpers
// ---------------------------------------------------------------------------
function exportSavedResult(result, currency) {
  if (!result?.result_data) return
  const rd = result.result_data
  if (result.result_type === 'cba') exportCBAResults(rd, currency)
  else if (result.result_type === 'kpis') exportLongTermResults(rd.withInvestment || rd, currency, rd.existingOnly || null)
  else if (result.result_type === 'optimization') exportOptimizationResults(rd, result.created_at, currency)
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
function PlanningResultModal({ show, onHide, scenarioId, communityId, scenarioName, currency = 'EUR' }) {
  const { canEdit } = useAuth()
  const { t } = useTranslation()
  const typeLabels = {
    kpis: t('results.type_long_term_kpis'),
    cba: t('results.type_cba'),
    optimization: t('results.type_optimization'),
    forecast: t('results.type_forecast'),
  }
  const [results, setResults] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  const fetchResults = useCallback(async () => {
    if (!communityId || !scenarioId) return
    setIsLoading(true)
    setError(null)
    try {
      const allResults = await resultsAPI.list(communityId)
      const arr = Array.isArray(allResults) ? allResults : []
      const filtered = arr.filter(r => r.parameters?.scenario_id === scenarioId)

      // Load full data for each result (list may not include result_data)
      const fullResults = await Promise.all(
        filtered.map(async (r) => {
          if (r.result_data && Object.keys(r.result_data).length > 0) return r
          try { return await resultsAPI.get(communityId, r.id) } catch { return r }
        })
      )
      setResults(fullResults)
    } catch (err) {
      console.error('Failed to fetch saved results:', err)
      setError(t('result_modal.error_load'))
      setResults([])
    } finally {
      setIsLoading(false)
    }
  }, [communityId, scenarioId])

  useEffect(() => {
    if (show) fetchResults()
    else { setResults([]); setError(null) }
  }, [show, fetchResults])

  const handleDeleteResult = async (resultId) => {
    if (!window.confirm(t('result_modal.confirm_delete'))) return
    try {
      await resultsAPI.delete(communityId, resultId)
      const updated = results.filter(r => r.id !== resultId)
      setResults(updated)
      if (updated.length === 0) onHide()
    } catch (err) {
      console.error('Failed to delete result:', err)
      alert(t('result_modal.alert_delete_failed'))
    }
  }

  // Group results by type
  const grouped = {}
  results.forEach(r => {
    const type = r.result_type || 'other'
    if (!grouped[type]) grouped[type] = []
    grouped[type].push(r)
  })
  const types = Object.keys(grouped)

  const renderResult = (result) => {
    const rt = result.result_type
    if (rt === 'kpis') return <KPIResultDetail result={result} currency={currency} />
    if (rt === 'cba') return <CBAResultDetail result={result} currency={currency} />
    if (rt === 'optimization') return <OptimizationResultDetail result={result} currency={currency} />
    return (
      <Card style={{ border: '1px solid var(--color-neutral-200)' }}>
        <Card.Body>
          <pre style={{ fontSize: 'var(--font-size-xs)', maxHeight: '400px', overflow: 'auto' }}>
            {JSON.stringify(result.result_data, null, 2)}
          </pre>
        </Card.Body>
      </Card>
    )
  }

  const renderTypeSection = (type, resultsList) => {
    const latest = resultsList[0]
    const tbc = typeBadgeColors[type] || typeBadgeColors.optimization
    return (
      <div key={type} className="mb-4">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div className="d-flex align-items-center gap-2">
            <h5 style={{ fontWeight: 600, margin: 0 }}>{typeLabels[type] || type}</h5>
            <Badge bg="none" style={{ backgroundColor: tbc.bg, color: tbc.color, fontSize: '0.7rem', fontWeight: 600 }}>
              {latest.created_at ? new Date(latest.created_at).toLocaleDateString() : ''}
            </Badge>
          </div>
          <div className="d-flex gap-2">
            <Button variant="outline-secondary" size="sm" onClick={() => exportSavedResult(latest, currency)} style={{ fontSize: 'var(--font-size-xs)' }}>
              {t('results.export_csv')}
            </Button>
            {canEdit && (
              <Button variant="outline-danger" size="sm" onClick={() => handleDeleteResult(latest.id)} style={{ fontSize: 'var(--font-size-xs)' }}>
                {t('results.delete_btn')}
              </Button>
            )}
          </div>
        </div>
        {renderResult(latest)}
      </div>
    )
  }

  return (
    <Modal show={show} onHide={onHide} size="xl" fullscreen="lg-down" scrollable>
      <Modal.Header closeButton style={{ borderBottom: '2px solid var(--color-primary, #8CB808)' }}>
        <Modal.Title style={{ fontSize: 'var(--font-size-lg)', fontWeight: 600 }}>
          {scenarioName || t('result_modal.modal_default_title')}
          <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-neutral-500)', fontWeight: 400, marginLeft: 'var(--space-3)' }}>
            {t('result_modal.modal_subtitle')}
          </span>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ backgroundColor: 'var(--color-neutral-50)', padding: 'var(--space-4)' }}>
        {isLoading && (
          <div className="text-center py-5">
            <Spinner animation="border" style={{ color: 'var(--color-primary)' }} />
            <p className="mt-3" style={{ color: 'var(--color-neutral-600)' }}>{t('result_modal.loading')}</p>
          </div>
        )}

        {error && <Alert variant="warning" style={{ fontSize: 'var(--font-size-sm)' }}>{error}</Alert>}

        {!isLoading && !error && results.length === 0 && (
          <div className="text-center py-5" style={{ color: 'var(--color-neutral-500)' }}>
            <p style={{ fontSize: 'var(--font-size-base)', fontWeight: 500 }}>
              {t('result_modal.no_results_title')}
            </p>
            <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-neutral-400)' }}>
              {t('result_modal.no_results_text')}
            </p>
          </div>
        )}

        {!isLoading && types.length > 0 && (
          types.length === 1
            ? renderTypeSection(types[0], grouped[types[0]])
            : (
              <Tabs defaultActiveKey={types[0]} className="mb-4" fill>
                {types.map(type => (
                  <Tab key={type} eventKey={type} title={
                    <span>
                      {typeLabels[type] || type}
                      <Badge bg="none" className="ms-1" style={{ backgroundColor: typeBadgeColors[type]?.bg || '#E3F2FD', color: typeBadgeColors[type]?.color || '#1565C0', fontSize: '0.65rem' }}>
                        {grouped[type].length}
                      </Badge>
                    </span>
                  }>
                    {renderTypeSection(type, grouped[type])}
                  </Tab>
                ))}
              </Tabs>
            )
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="primary" size="sm" onClick={onHide} style={{ backgroundColor: 'var(--color-primary)', borderColor: 'var(--color-primary)' }}>
          {t('results.close')}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default PlanningResultModal
